import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const PERSONAS = [
  {
    to: '/solutions/brokers',
    tone: 'persona-card-pink',
    img: 'https://images.unsplash.com/photo-1551836022-4c4c79ecde51?fm=jpg&q=70&w=1400&auto=format&fit=crop',
    alt: 'Two advisors talking with a client across a table',
    title: 'Brokers & Consultants',
    desc: 'Avenir handles the analysis so your team can focus on strategy and clients.',
  },
  {
    to: '/solutions/employers',
    tone: 'persona-card-purple',
    img: 'https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?fm=jpg&q=70&w=1400&auto=format&fit=crop',
    alt: 'An HR team working together around a table',
    title: 'Self-Insured HR Leaders',
    desc: 'Your largest controllable expense, with Fortune 500-caliber analytics on your own terms.',
  },
  {
    to: '/product',
    tone: 'persona-card-purple',
    img: 'https://images.unsplash.com/photo-1713947506827-c646da3ad1db?fm=jpg&q=70&w=1400&auto=format&fit=crop',
    alt: 'Two colleagues celebrating at a desk over a laptop',
    title: 'Account & Client Service',
    desc: 'Stewardship decks, open enrollment kits, and monthly reporting, produced and branded without the scramble.',
  },
  {
    to: '/solutions/cfos',
    tone: 'persona-card-amber',
    img: 'https://images.unsplash.com/photo-1785140629289-907350b7e3bb?fm=jpg&q=70&w=1400&auto=format&fit=crop',
    alt: 'Hands pointing at financial charts on a printed report',
    title: 'CFOs & Finance Teams',
    desc: 'Actuarial-grade projections you can stand behind, with the rigor of any other budget line.',
  },
];

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

/* The heading stays pinned while vertical scroll drives the card row
   sideways. Travel is measured from the track itself, so the row always
   ends flush with the right edge no matter how wide the cards render. */
const PersonaCards = () => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const viewRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const track = trackRef.current;
    const view = viewRef.current;
    if (!wrap || !track || !view) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pinned = window.matchMedia('(min-width: 901px)');

    const apply = () => {
      if (!pinned.matches || reduced.matches) {
        track.style.transform = '';
        return;
      }
      const rect = wrap.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const p = travel > 0 ? clamp01(-rect.top / travel) : 0;
      const max = Math.max(track.scrollWidth - view.clientWidth, 0);
      track.style.transform = `translate3d(${-(p * max).toFixed(2)}px, 0, 0)`;
    };

    // Synchronous for the same reason as the workflow scroller: animation
    // frames do not run while the tab is not being painted.
    const onScroll = apply;

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('visibilitychange', onScroll);
    reduced.addEventListener?.('change', onScroll);
    pinned.addEventListener?.('change', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', onScroll);
      reduced.removeEventListener?.('change', onScroll);
      pinned.removeEventListener?.('change', onScroll);
    };
  }, []);

  return (
    <div className="ps-scroll" ref={wrapRef}>
      <div className="ps-sticky">
        <div className="ps-head">
          <h2 className="section-heading">
            Built for the people who <span className="gradient-text">run benefits</span>
          </h2>
        </div>

        <div className="ps-viewport" ref={viewRef}>
          <div className="ps-track" ref={trackRef}>
            {PERSONAS.map((p) => (
              <Link key={p.to} to={p.to} className={`persona-card ${p.tone}`}>
                <div className="persona-media">
                  {/* not lazy: the off-screen cards are moved by transform, so the
                      browser's lazy heuristics can leave them permanently unloaded */}
                  <img src={p.img} alt={p.alt} />
                </div>
                <h3 className="persona-title">{p.title}</h3>
                <p className="persona-desc">{p.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonaCards;
